import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  HardHat, ChevronDown, Plus, X, Camera, Loader2, CheckCircle2, AlertTriangle,
  Clock, PenLine, ShieldCheck, Search, Trash2, FileText, MapPin, ExternalLink,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { useReturnTo } from "@/lib/returnTo";
import { useGps } from "@/features/gps/useGps";
import { GpsIndicator } from "@/features/gps/GpsIndicator";
import { Card, Pill, Button, Input, Select, Textarea, Spinner, EmptyState } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { uploadHseEvidence } from "@/features/hse/evidenceUpload";
import { SignaturePad, type SignaturePadHandle } from "@/features/signature/SignaturePad";
import { useOpenStanddowns, stopCovering } from "@/features/hse/standdownShared";   // mig 425

// =============================================================================
// Daily Start Card — T-425 (full card: sections 1-9)
// =============================================================================
// Benchmarked against Ventia's Novus Start Card and rebuilt to James's
// 2026-08-03 review ("this will end up with us in jail if wrong"): sectioned
// controlled form, Y/N risk screens that EXPAND into requirements and
// auto-flag permits, rescue plan capture for heights work (falls CoP requires
// emergency/rescue procedures wherever fall-arrest is relied on), BYDA before
// ground penetration, environmental controls, traffic management with
// implement/verify accountability, first-aid kit + extinguisher PHOTO
// evidence, nearest-hospital as first-class data (site-remembered + a
// find-on-maps helper), and the fit-for-work sign-on declaration.
// Warn-not-block at submit — but the warnings NAME every gap.
// 8 Oct 2026 (mig 425): a field login cannot START a card on a site that is
// stood down or under a carrier embargo — the database refuses it with the
// reason (James: "that's a good control"); a manager still can, for a
// make-safe visit. The 7am board shows such a site as STOOD DOWN, not NO CARD.
// 8 Oct 2026 (Ops brief 6, gap 6, no migration — the answers live in
// risk_answers.screens.exclusion): a Yes to "Exclusion / drop zones needed?"
// now asks for what Ventia audits on a prestart — the comms method (Coretel's
// rule is 2-way radios), the named ground safety observer, the inner and wider
// zone sizes, and everyone allowed in the inner zone with their position and
// exit route. Missing ones are named in the submit warnings, like every other gap.
// =============================================================================

const PERMIT_TYPES = ["hot_work", "excavation", "workbox", "confined_space", "working_at_heights", "live_electrical"] as const;

const DECLARATION = [
  "I have been inducted to this job site.",
  "I actively participated in identifying and controlling the risks of today's work in this pre-start briefing.",
  "The SWMS listed on this start card have been communicated to me; I understand them and will comply with them.",
  "I will implement the identified control measures, including the required PPE.",
  "If I don't understand a control, or site conditions change, work stops and guidance is sought before continuing.",
  "If a permit is identified on this card, I have been inducted on its requirements and will work to its conditions.",
  "I am not under the influence of drugs or alcohol, and I am fit to perform the required tasks — aware, rested, alert and capable.",
];

const MANDATORY_PPE = ["Hi-vis clothing", "Long pants + long sleeves", "Steel cap boots", "Safety glasses", "Gloves", "Hard hat / helmet"];
const EXTRA_PPE = ["Hearing protection", "Harness + lanyard", "RF monitor", "Respirator / P2", "Face shield", "Sun protection"];

// The Y/N risk screens. Each YES expands its requirements; `permit` links the
// answer to the permit section. Order mirrors how a site walk actually goes.
const RISK_SCREENS: { key: string; q: string; permit?: string; detail?: { key: string; label: string; kind: "text" | "yesno"; required?: boolean }[] }[] = [
  { key: "variance", q: "Any variance from the approved plans / scope / LASE / heritage or environmental requirements?", detail: [
    { key: "detail", label: "What's varied and who approved it", kind: "text", required: true },
  ]},
  { key: "heights", q: "Working at height today (>2m, or any fall risk)?", permit: "working_at_heights", detail: [
    { key: "rescue_plan", label: "Rescue plan in place and briefed (falls CoP — required wherever fall-arrest is relied on)", kind: "yesno", required: true },
    { key: "rescue_detail", label: "Rescue plan reference / summary (who, how, equipment on site)", kind: "text", required: true },
    { key: "harness_ok", label: "Harnesses + lanyards inspected before use", kind: "yesno" },
  ]},
  { key: "excavation", q: "Ground penetration / excavation today (including pickets and augering)?", permit: "excavation", detail: [
    { key: "byda_ref", label: "BYDA (Dial Before You Dig) reference + plans on site", kind: "text", required: true },
    { key: "services_located", label: "Underground services located / potholed", kind: "yesno" },
  ]},
  { key: "confined", q: "Entering a pit, manhole, tank or confined space?", permit: "confined_space", detail: [
    { key: "gas_detection", label: "Gas detection available and bump-tested", kind: "yesno", required: true },
    { key: "standby", label: "Standby person assigned", kind: "yesno" },
  ]},
  { key: "hot_work", q: "Hot work today (welding, cutting, grinding, naked flame)?", permit: "hot_work", detail: [
    { key: "fire_watch", label: "Fire watch + extinguisher at the work front", kind: "yesno", required: true },
  ]},
  { key: "electrical", q: "Work on or near live electrical / requiring isolation?", permit: "live_electrical", detail: [
    { key: "isolation_ref", label: "Isolation / permit reference + locks and tags applied", kind: "text", required: true },
  ]},
  { key: "plant_q", q: "Plant on site today (excavator, EWP, crane, vac truck…)?", detail: [] },   // expands section 6
  { key: "exclusion", q: "Exclusion / drop zones needed?", detail: [
    { key: "detail", label: "Where, how demarcated, and who monitors", kind: "text", required: true },
  ]},
  { key: "ground", q: "Slip / trip hazards or poor ground conditions (mud, cables, uneven, holes)?", detail: [
    { key: "detail", label: "Hazards + controls (barricade, reroute, housekeeping)", kind: "text", required: true },
  ]},
  { key: "public", q: "Public interface or traffic today (roads, footpaths, live premises)?", detail: [] },  // expands section 7
];

const ENV_CONTROLS = [
  { key: "dust", label: "Dust controls" },
  { key: "noise", label: "Noise controls" },
  { key: "water", label: "Water / runoff controls" },
  { key: "sediment", label: "Sediment controls" },
  { key: "fuel", label: "Fuel / lubricant handling (bunding, trays)" },
  { key: "waste", label: "Waste requiring disposal" },
];

type TaskRow = { description: string; permit_type?: string | null };
// A card can carry either a base template OR (preferably) the project's
// approved site-specific SWMS. project_swms_id set = site SWMS; swms_id is
// then the base template it came from, so old cards still read fine.
type SwmsPick = {
  swms_id: string; swms_code: string; display_name: string; version?: string | null;
  project_swms_id?: string | null; site_specific?: boolean;
};
type PermitLink = { permit_type: string; permit_id?: string | null; permit_number?: string | null };

type CardRow = {
  card_id: string; card_number: string; project_id: string; site_id: string | null;
  card_date: string; status: string;
  supervisor_employee_id: string | null; supervisor_phone: string | null;
  start_time: string | null; work_area: string | null;
  tasks: TaskRow[]; swms: SwmsPick[]; setup_photos: any[];
  first_aider_employee_id: string | null; first_aider_name: string | null; first_aider_contact: string | null;
  first_aid_kit_onsite: boolean | null; evacuation_point: string | null;
  nearest_hospital: string | null; nearest_hospital_phone: string | null;
  risk_answers: any; ppe: any; plant: any; traffic: any; extra_hazards: any[];
  gps_lat: number | null; gps_lng: number | null; weather: string | null;
  submitted_at: string | null;
  projects?: { project_code: string; project_name: string; site_id: string | null; sites: { site_name: string; nearest_hospital: string | null; nearest_hospital_phone: string | null; evacuation_point: string | null } | null } | null;
};

type SignonRow = {
  signon_id: string; name: string; company: string | null; role_title: string | null;
  signature_svg: string | null; signed_at: string;
};

type EmployeeOpt = { employee_id: string; full_name: string; mobile: string | null };

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}
function fmtTime(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
}

function useEmployees() {
  return useQuery({
    queryKey: ["employees_with_mobile"],
    queryFn: async () => {
      const { data } = await supabase.from("employees")
        .select("employee_id, first_name, last_name, mobile, is_active")
        .eq("is_active", true).order("first_name");
      return ((data ?? []) as any[]).map(e => ({
        employee_id: e.employee_id,
        full_name: `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
        mobile: e.mobile ?? null,
      })) as EmployeeOpt[];
    },
  });
}

// =============================================================================
// Routing shell
// =============================================================================
export default function StartCardScreen() {
  const { cardId } = useParams<{ cardId: string }>();
  if (cardId) return <StartCardEditor cardId={cardId}/>;
  return <StartCardList/>;
}

// =============================================================================
// List view
// =============================================================================
function StartCardList() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const [starting, setStarting] = useState<null | { projectId?: string }>(null);

  const today = new Date().toISOString().slice(0, 10);

  // T-425 slice 4 — the board: every underway project with a crew window
  // covering today, so "on site with no card" is visible at 7am.
  const { data: board = [] } = useQuery({
    queryKey: ["start_card_board"],
    queryFn: async () => {
      const { data: asg } = await supabase.from("project_crew_assignments")
        .select("project_id, assigned_from, assigned_to, crews:crew_id(crew_code), projects:project_id(project_id, project_code, project_name, status, sites:site_id(site_name))")
        .lte("assigned_from", today)
        .or(`assigned_to.gte.${today},assigned_to.is.null`);
      const byProject = new Map<string, any>();
      for (const a of (asg ?? []) as any[]) {
        const p = a.projects;
        if (!p || !["live", "mobilising", "acceptance_pending"].includes(p.status)) continue;
        const e = byProject.get(p.project_id) ?? { ...p, crewCodes: [] as string[] };
        if (a.crews?.crew_code && !e.crewCodes.includes(a.crews.crew_code)) e.crewCodes.push(a.crews.crew_code);
        byProject.set(p.project_id, e);
      }
      return [...byProject.values()];
    },
  });
  const { data: standData } = useOpenStanddowns();   // mig 425 — "STOOD DOWN", not "NO CARD"
  const { data: cards = [], isLoading } = useQuery({
    queryKey: ["start_cards_recent"],
    queryFn: async () => {
      const since = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10);
      const { data, error } = await supabase.from("hse_start_cards")
        .select("card_id, card_number, project_id, card_date, status, work_area, submitted_at, projects:project_id(project_code, project_name, sites:site_id(site_name))")
        .gte("card_date", since)
        .order("card_date", { ascending: false }).order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: signonCounts = new Map() } = useQuery({
    queryKey: ["start_card_signon_counts", cards.map(c => c.card_id).join(",")],
    enabled: cards.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("hse_start_card_signons")
        .select("card_id").in("card_id", cards.map(c => c.card_id));
      const m = new Map<string, number>();
      for (const r of data ?? []) m.set((r as any).card_id, (m.get((r as any).card_id) ?? 0) + 1);
      return m;
    },
  });

  const todayCards = cards.filter(c => c.card_date === today);
  const earlier = cards.filter(c => c.card_date !== today);

  return (
    <div className="p-4 md:p-6 max-w-[900px] mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">HSE</div>
          <h1 className="text-2xl font-semibold tracking-tight">Start cards</h1>
          <p className="text-text-muted text-[13px] mt-1">
            The daily authority to work — scope, risk screens, permits, first aid, crew sign-on. One card per crew per day.
          </p>
        </div>
        <Button icon={Plus} onClick={() => setStarting({})}>Start today's card</Button>
      </div>

      {/* The board — crews on site today vs cards */}
      {board.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">On site today (per the schedule)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {board.map((p: any) => {
              const c = cards.find(x => x.project_id === p.project_id && x.card_date === today);
              const state = c?.status === "submitted" ? "ok" : c ? "draft" : "none";
              // Mig 425 — a stood-down site with no card is not a missing card:
              // nobody should be starting one. (A manager's make-safe card still shows.)
              const stood = !c ? stopCovering(standData, p.project_id, today, today) : null;
              if (stood) return (
                <button key={p.project_id} type="button" onClick={() => navigate(`/hse/standdowns/${stood.standdown_id}`)}
                  className="text-left rounded-md border border-line bg-ink-card px-3 py-2.5 transition hover:border-danger/50"
                  title={`${stood.standdown_number} — ${stood.reason}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-accent">{p.project_code}</span>
                    <span className="text-[12px] text-text flex-1 truncate">{p.sites?.site_name ?? p.project_name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-text-muted">{p.crewCodes.join(" + ") || "crew"}</span>
                    <span className="ml-auto text-[10px] font-mono uppercase tracking-wider2 text-danger font-semibold">STOOD DOWN · {stood.standdown_number}</span>
                  </div>
                </button>
              );
              return (
                <button key={p.project_id} type="button"
                  onClick={() => c ? navigate(`/hse/start-card/${c.card_id}`) : setStarting({ projectId: p.project_id })}
                  className={`text-left rounded-md border px-3 py-2.5 transition hover:brightness-110 ${
                    state === "ok" ? "border-ok/40 bg-ok/8" : state === "draft" ? "border-warn/50 bg-warn/10" : "border-danger/50 bg-danger/10"}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-accent">{p.project_code}</span>
                    <span className="text-[12px] text-text flex-1 truncate">{p.sites?.site_name ?? p.project_name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-text-muted">{p.crewCodes.join(" + ") || "crew"}</span>
                    <span className={`ml-auto text-[10px] font-mono uppercase tracking-wider2 ${
                      state === "ok" ? "text-ok" : state === "draft" ? "text-warn" : "text-danger font-semibold"}`}>
                      {state === "ok" ? "card submitted" : state === "draft" ? "draft" : "NO CARD"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isLoading && <div className="flex justify-center py-10"><Spinner size={20}/></div>}

      {!isLoading && (
        <>
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Today</div>
          {todayCards.length === 0 && (
            <EmptyState icon={HardHat} title="No card yet today"
              description="Crews on site should have a start card before work begins. Hit 'Start today's card' — yesterday's crew, SWMS and tasks pre-fill."/>
          )}
          {todayCards.map(c => <CardListRow key={c.card_id} c={c} count={(signonCounts as Map<string, number>).get(c.card_id) ?? 0} onOpen={() => navigate(`/hse/start-card/${c.card_id}`)}/>) }

          {earlier.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-text-muted pt-2">Last 7 days</div>
              {earlier.map(c => <CardListRow key={c.card_id} c={c} count={(signonCounts as Map<string, number>).get(c.card_id) ?? 0} onOpen={() => navigate(`/hse/start-card/${c.card_id}`)}/>) }
            </>
          )}
        </>
      )}

      {starting && (
        <NewCardModal
          userId={user?.user_id ?? null}
          initialProjectId={starting.projectId}
          onClose={() => setStarting(null)}
          onCreated={id => { setStarting(null); navigate(`/hse/start-card/${id}`); }}
        />
      )}
    </div>
  );
}

function CardListRow({ c, count, onOpen }: { c: any; count: number; onOpen: () => void }) {
  return (
    <Card>
      <button type="button" onClick={onOpen} className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap hover:bg-ink-raised/40 transition">
        <span className="font-mono text-[12px] text-accent w-[92px]">{c.card_number}</span>
        <div className="flex-1 min-w-[200px]">
          <div className="text-[13px] text-text">{c.projects?.project_code} · {c.projects?.sites?.site_name ?? c.projects?.project_name}</div>
          {c.work_area && <div className="text-[11px] text-text-muted truncate max-w-[420px]">{c.work_area}</div>}
        </div>
        <span className="text-[12px] text-text-muted">{fmtDate(c.card_date)}</span>
        <span className="text-[12px] text-text-muted inline-flex items-center gap-1"><PenLine size={11}/> {count}</span>
        <Pill label={c.status} tone={c.status === "submitted" ? "ok" : c.status === "draft" ? "pending" : "closed"} small/>
      </button>
    </Card>
  );
}

// =============================================================================
// New card modal — project pick + prefill
// =============================================================================
function NewCardModal({ userId, initialProjectId, onClose, onCreated }: { userId: string | null; initialProjectId?: string; onClose: () => void; onCreated: (id: string) => void }) {
  const [projectId, setProjectId] = useState(initialProjectId ?? "");
  const [prefill, setPrefill] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: projects = [] } = useQuery({
    queryKey: ["start_card_projects"],
    queryFn: async () => {
      const { data } = await supabase.from("projects")
        .select("project_id, project_code, project_name, site_id, sites:site_id(site_name)")
        .in("status", ["scheduled", "won", "mobilising", "live", "on_hold", "acceptance_pending"])
        .order("project_code", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const { data: lastCard } = useQuery({
    queryKey: ["start_card_last", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await supabase.from("hse_start_cards")
        .select("card_id, card_date, tasks, swms, work_area, supervisor_employee_id, supervisor_phone, first_aider_employee_id, first_aider_name, first_aider_contact, evacuation_point, nearest_hospital, nearest_hospital_phone, first_aid_kit_onsite, risk_answers, ppe, plant, traffic")
        .eq("project_id", projectId)
        .order("card_date", { ascending: false }).order("created_at", { ascending: false })
        .limit(1).maybeSingle();
      return data as any | null;
    },
  });

  async function create() {
    if (!projectId) return;
    setBusy(true); setErr(null);
    try {
      const project = projects.find(p => p.project_id === projectId);
      const base: any = { project_id: projectId, site_id: project?.site_id ?? null, created_by: userId };
      if (prefill && lastCard) {
        Object.assign(base, {
          tasks: (lastCard.tasks ?? []).map((t: TaskRow) => ({ description: t.description, permit_type: t.permit_type ?? null })),
          swms: lastCard.swms ?? [],
          work_area: lastCard.work_area,
          supervisor_employee_id: lastCard.supervisor_employee_id,
          supervisor_phone: lastCard.supervisor_phone,
          first_aider_employee_id: lastCard.first_aider_employee_id,
          first_aider_name: lastCard.first_aider_name,
          first_aider_contact: lastCard.first_aider_contact,
          evacuation_point: lastCard.evacuation_point,
          nearest_hospital: lastCard.nearest_hospital,
          nearest_hospital_phone: lastCard.nearest_hospital_phone,
          first_aid_kit_onsite: lastCard.first_aid_kit_onsite,
          ppe: lastCard.ppe ?? {},
          prefilled_from_card_id: lastCard.card_id,
          // Deliberately NOT prefilled: risk answers, permits, plant, traffic —
          // those are today's conditions and must be answered fresh.
        });
      }
      const { data, error } = await supabase.from("hse_start_cards").insert(base).select("card_id").single();
      if (error) throw error;
      onCreated(data.card_id);
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[12vh]">
      <div className="bg-ink-raised border border-line rounded-lg w-full max-w-[480px]">
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">Start today's card</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-2.5 -m-1.5 rounded-md" aria-label="Close"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-4">
          {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
          <label className="block text-[12px] text-text-muted">Project / site
            <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
              <option value="">Pick…</option>
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.sites?.site_name ?? p.project_name}</option>)}
            </Select>
          </label>
          {lastCard && (
            <label className="flex items-start gap-2 text-[13px] cursor-pointer">
              <input type="checkbox" checked={prefill} onChange={e => setPrefill(e.target.checked)} className="mt-0.5"/>
              <span>
                Same works as {fmtDate(lastCard.card_date)} — pre-fill crew details, SWMS, tasks and PPE
                <span className="block text-[11px] text-text-muted">Risk screens, permits, plant and traffic are answered fresh — they're today's conditions. Everyone signs on fresh too.</span>
              </span>
            </label>
          )}
          <Button onClick={create} disabled={!projectId || busy} icon={busy ? Loader2 : Plus} className="w-full justify-center">
            {busy ? "Creating…" : "Create card"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Editor
// =============================================================================
function StartCardEditor({ cardId }: { cardId: string }) {
  const user = useRequireAuth();
  const canManage = hasRole(user, "manager");
  const returnTo = useReturnTo();   // 27 Sep — opened from a Site folder? go back there
  const qc = useQueryClient();
  const gps = useGps(true);
  const navigate = useNavigate();

  const { data: card, isLoading } = useQuery({
    queryKey: ["start_card", cardId],
    queryFn: async () => {
      const { data, error } = await supabase.from("hse_start_cards")
        .select(`*, projects:project_id(project_code, project_name, site_id, sites:site_id(site_name, nearest_hospital, nearest_hospital_phone, evacuation_point))`)
        .eq("card_id", cardId).single();
      if (error) throw error;
      return data as unknown as CardRow;
    },
  });

  const { data: signons = [], refetch: refetchSignons } = useQuery({
    queryKey: ["start_card_signons", cardId],
    queryFn: async () => {
      const { data, error } = await supabase.from("hse_start_card_signons")
        .select("signon_id, name, company, role_title, signature_svg, signed_at")
        .eq("card_id", cardId).order("signed_at");
      if (error) throw error;
      return (data ?? []) as SignonRow[];
    },
  });

  // Open permits on this project — for linking into section 4.
  const { data: projectPermits = [], refetch: refetchPermits } = useQuery({
    queryKey: ["start_card_permits", card?.project_id],
    enabled: !!card?.project_id,
    queryFn: async () => {
      const { data } = await supabase.from("permits")
        .select("permit_id, permit_number, permit_type, status, valid_from, valid_until")
        .eq("project_id", card!.project_id)
        .in("status", ["draft", "issued", "active"])
        .order("issued_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  // ---- Draft state (ref-mirrored, debounced autosave) ----
  const [draft, setDraft] = useState<Partial<CardRow> | null>(null);
  const draftRef = useRef<Partial<CardRow> | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "dirty" | "saving" | "saved" | "error">("idle");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (card && !draft) {
      const init: Partial<CardRow> = {
        supervisor_employee_id: card.supervisor_employee_id,
        supervisor_phone: card.supervisor_phone,
        start_time: card.start_time,
        work_area: card.work_area,
        tasks: card.tasks ?? [],
        swms: card.swms ?? [],
        setup_photos: card.setup_photos ?? [],
        first_aider_employee_id: card.first_aider_employee_id,
        first_aider_name: card.first_aider_name,
        first_aider_contact: card.first_aider_contact,
        first_aid_kit_onsite: card.first_aid_kit_onsite,
        evacuation_point: card.evacuation_point ?? card.projects?.sites?.evacuation_point ?? null,
        nearest_hospital: card.nearest_hospital ?? card.projects?.sites?.nearest_hospital ?? null,
        nearest_hospital_phone: card.nearest_hospital_phone ?? card.projects?.sites?.nearest_hospital_phone ?? null,
        risk_answers: card.risk_answers ?? {},
        ppe: card.ppe ?? {},
        plant: card.plant ?? {},
        traffic: card.traffic ?? {},
        extra_hazards: card.extra_hazards ?? [],
        weather: card.weather,
      };
      draftRef.current = init;
      setDraft(init);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card]);

  const readOnly = card?.status === "submitted" || card?.status === "closed";

  function patchDraft(fields: Partial<CardRow>) {
    if (readOnly) return;
    const merged = { ...(draftRef.current ?? draft ?? {}), ...fields };
    draftRef.current = merged;
    setDraft(merged);
    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void saveDraft(draftRef.current ?? merged); }, 1800);
  }

  // jsonb helpers — nested-object merges for the risk battery etc.
  const RA = (draft?.risk_answers ?? {}) as any;
  function patchRisk(patch: any) { patchDraft({ risk_answers: { ...RA, ...patch } }); }
  function patchScreen(key: string, patch: any) {
    patchRisk({ screens: { ...(RA.screens ?? {}), [key]: { ...((RA.screens ?? {})[key] ?? {}), ...patch } } });
  }
  const screens = (RA.screens ?? {}) as Record<string, any>;
  const env = (RA.environment ?? {}) as Record<string, any>;
  const firstAid = (RA.first_aid ?? {}) as any;
  const permits: PermitLink[] = (RA.permits ?? []) as PermitLink[];

  async function saveDraft(fields?: Partial<CardRow>): Promise<boolean> {
    const payload = fields ?? draftRef.current ?? draft;
    if (!payload) return true;
    setSaveState("saving");
    const { error } = await supabase.from("hse_start_cards").update({
      ...payload,
      gps_lat: gps.fix?.latitude ?? card?.gps_lat,
      gps_lng: gps.fix?.longitude ?? card?.gps_lng,
    }).eq("card_id", cardId);
    if (error) { setSaveState("error"); return false; }
    setSaveState("saved");
    return true;
  }

  // Permit types flagged by YES answers (plus any tagged on tasks)
  const flaggedPermits = new Set<string>();
  for (const s of RISK_SCREENS) if (s.permit && screens[s.key]?.answer === true) flaggedPermits.add(s.permit);
  for (const t of ((draft?.tasks as TaskRow[]) ?? [])) if (t.permit_type) flaggedPermits.add(t.permit_type);

  function submitWarnings(): string[] {
    const w: string[] = [];
    if (!draft?.supervisor_employee_id) w.push("no supervisor set");
    if (!(draft?.swms as SwmsPick[] | undefined)?.length) w.push("no SWMS on the card");
    if (!draft?.first_aider_employee_id && !draft?.first_aider_name) w.push("no first aider");
    if (!draft?.nearest_hospital) w.push("nearest hospital not recorded");
    if (draft?.first_aid_kit_onsite !== true) w.push("first aid kit not confirmed");
    if (firstAid.extinguisher !== true && screens.hot_work?.answer === true) w.push("hot work without an extinguisher confirmed");
    if (screens.heights?.answer === true && screens.heights?.rescue_plan !== true) w.push("HEIGHTS WORK WITHOUT A RESCUE PLAN");
    if (screens.excavation?.answer === true && !screens.excavation?.byda_ref) w.push("excavation without a BYDA reference");
    // gap 6 — the inner-zone fields a client audit looks for
    if (screens.exclusion?.answer === true) {
      const ex = screens.exclusion ?? {};
      if (!ex.comms_method) w.push("exclusion zone without a comms method");
      if (!String(ex.observer ?? "").trim()) w.push("exclusion zone without a named ground observer");
      if (!String(ex.inner_zone_m ?? "").trim()) w.push("inner zone size not recorded");
      const ppl = Array.isArray(ex.inner_people) ? ex.inner_people : [];
      if (ppl.some((x: any) => String(x?.name ?? "").trim() && (!String(x?.position ?? "").trim() || !String(x?.exit_route ?? "").trim()))) w.push("someone in the inner zone without a position and exit route");
    }
    for (const pt of flaggedPermits) {
      if (!permits.find(p => p.permit_type === pt && p.permit_number)) w.push(`${pt.replace(/_/g, " ")} permit not linked`);
    }
    if (screens.public?.answer === true && !(draft?.traffic as any)?.tgs_ref) w.push("traffic/public interface without a TGS/TMP reference");
    if ((draft?.plant as any)?.used === true && !((draft?.plant as any)?.items ?? []).every((i: any) => i.prestart_done)) w.push("plant on site without pre-starts confirmed");
    if (signons.length === 0) w.push("nobody has signed on");
    return w;
  }

  async function submitCard() {
    const warnings = submitWarnings();
    if (warnings.length > 0 && !window.confirm(`Before you submit — gaps on this card:\n\n• ${warnings.join("\n• ")}\n\nSubmit anyway? (The gaps are recorded.)`)) return;
    if (!(await saveDraft())) return;
    const { error } = await supabase.from("hse_start_cards").update({
      status: "submitted",
      submitted_at: new Date().toISOString(),
      submitted_by_user_id: user?.user_id ?? null,
      start_time: draft?.start_time ?? new Date().toTimeString().slice(0, 5),
      risk_answers: { ...(draftRef.current?.risk_answers ?? {}), submit_warnings: warnings },
    }).eq("card_id", cardId);
    if (error) { window.alert(error.message); return; }
    qc.invalidateQueries({ queryKey: ["start_card", cardId] });
    qc.invalidateQueries({ queryKey: ["start_cards_recent"] });
  }

  async function reopen() {
    const { error } = await supabase.from("hse_start_cards").update({ status: "draft", submitted_at: null }).eq("card_id", cardId);
    if (error) { window.alert(error.message); return; }
    qc.invalidateQueries({ queryKey: ["start_card", cardId] });
  }

  if (isLoading || !card || !draft) return <div className="py-20 flex justify-center"><Spinner/></div>;

  const s1Done = !!(draft.supervisor_employee_id && (draft.work_area || (draft.tasks as TaskRow[])?.length) && (draft.swms as SwmsPick[])?.length);
  const s2Done = !!((draft.first_aider_employee_id || draft.first_aider_name) && draft.nearest_hospital && draft.first_aid_kit_onsite != null);
  const s3Done = RISK_SCREENS.every(s => screens[s.key]?.answer === true || screens[s.key]?.answer === false);
  const s4Done = [...flaggedPermits].every(pt => permits.find(p => p.permit_type === pt && p.permit_number));
  const s5Done = (draft.ppe as any)?.mandatory_confirmed === true;
  const s6Done = screens.plant_q?.answer === false || ((draft.plant as any)?.items ?? []).length > 0;
  const s7Done = screens.public?.answer === false || !!(draft.traffic as any)?.tgs_ref;
  const s8Done = screens.visual?.answer != null;
  const s9Done = signons.length > 0;

  const uploadPhoto = async (files: File[], prefix: string) => {
    const out: any[] = [];
    for (const f of files) {
      const res = await uploadHseEvidence(f, {
        project_code: card.projects?.project_code,
        employee_name: user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() : undefined,
      }, gps.fix, prefix);
      out.push(res);
    }
    return out;
  };

  return (
    <div className="p-4 md:p-6 max-w-[880px] mx-auto pb-32 space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to={returnTo ?? "/hse/start-card"} className="text-[12px] text-text-muted hover:text-text">← {returnTo ? "Site folder" : "Start cards"}</Link>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="font-mono text-[13px] text-accent">{card.card_number}</span>
            <Pill label={card.status} tone={card.status === "submitted" ? "ok" : "pending"}/>
            <span className="text-[12px] text-text-muted">{fmtDate(card.card_date)}</span>
          </div>
          <h1 className="text-xl font-semibold tracking-tight mt-0.5">
            {card.projects?.project_code} · {card.projects?.sites?.site_name ?? card.projects?.project_name}
          </h1>
        </div>
        <div className="text-right space-y-1">
          <GpsIndicator gps={gps}/>
          <div className="text-[11px] text-text-muted">
            {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Draft saved" : saveState === "dirty" ? "Unsaved changes" : saveState === "error" ? <span className="text-danger">Save failed — retry below</span> : ""}
          </div>
        </div>
      </div>

      {readOnly && (
        <div className="bg-ok/8 border border-ok/30 rounded-md px-3 py-2 text-[12px] text-ok flex items-center gap-2 flex-wrap">
          <CheckCircle2 size={13}/> Submitted {fmtTime(card.submitted_at)} — the card is locked.
          {canManage && <button type="button" onClick={reopen} className="text-accent hover:underline ml-auto">Reopen (manager)</button>}
        </div>
      )}

      {/* ============ 1 · AUTHORITY TO WORK ============ */}
      <CardSection n="1" title="Authority to work" done={s1Done} defaultOpen>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EmployeeSelect label="Site / team supervisor"
            value={draft.supervisor_employee_id ?? ""}
            disabled={readOnly}
            onPick={(id, mobile) => patchDraft({ supervisor_employee_id: id || null, supervisor_phone: mobile ?? draft.supervisor_phone })}/>
          <label className="block text-[12px] text-text-muted">Supervisor phone
            <Input value={draft.supervisor_phone ?? ""} onChange={e => patchDraft({ supervisor_phone: e.target.value })} disabled={readOnly} placeholder="04xx xxx xxx"/>
          </label>
        </div>
        <div className="flex items-end gap-2">
          <label className="block text-[12px] text-text-muted flex-1">Start time
            <Input type="time" value={draft.start_time ?? ""} onChange={e => patchDraft({ start_time: e.target.value })} disabled={readOnly}/>
          </label>
          {!readOnly && (
            <Button size="sm" variant="secondary" icon={Clock} onClick={() => patchDraft({ start_time: new Date().toTimeString().slice(0, 5) })}>Now</Button>
          )}
        </div>
        <label className="block text-[12px] text-text-muted">Describe the work area
          <Textarea rows={2} value={draft.work_area ?? ""} onChange={e => patchDraft({ work_area: e.target.value })} disabled={readOnly}
            placeholder="e.g. Compound + tower base, lattice tower faces K and W"/>
        </label>

        <div>
          <div className="text-[12px] text-text-muted mb-1.5">Tasks to be completed today</div>
          {((draft.tasks as TaskRow[]) ?? []).map((t, i) => (
            <div key={i} className="flex items-center gap-2 mb-1.5">
              <Input value={t.description} disabled={readOnly} placeholder="What's being done"
                onChange={e => {
                  const tasks = [...(draft.tasks as TaskRow[])];
                  tasks[i] = { ...tasks[i], description: e.target.value };
                  patchDraft({ tasks });
                }}/>
              <Select value={t.permit_type ?? ""} disabled={readOnly} className="w-[180px]"
                onChange={e => {
                  const tasks = [...(draft.tasks as TaskRow[])];
                  tasks[i] = { ...tasks[i], permit_type: e.target.value || null };
                  patchDraft({ tasks });
                }}>
                <option value="">No permit needed</option>
                {PERMIT_TYPES.map(p => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
              </Select>
              {!readOnly && (
                <button type="button" onClick={() => patchDraft({ tasks: (draft.tasks as TaskRow[]).filter((_, ti) => ti !== i) })}
                  className="text-text-muted hover:text-danger p-1"><Trash2 size={13}/></button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button size="sm" variant="secondary" icon={Plus}
              onClick={() => patchDraft({ tasks: [...((draft.tasks as TaskRow[]) ?? []), { description: "" }] })}>
              Add task
            </Button>
          )}
        </div>

        <SwmsPicker value={(draft.swms as SwmsPick[]) ?? []} onChange={v => patchDraft({ swms: v })}
          disabled={readOnly} projectId={card.project_id}/>

        <PhotoStrip label="Site set-up photos (compound, signage, access)"
          photos={(draft.setup_photos as any[]) ?? []}
          disabled={readOnly}
          onAdd={async files => {
            const added = await uploadPhoto(files, `start-cards/${cardId}/setup`);
            patchDraft({ setup_photos: [...((draft.setup_photos as any[]) ?? []), ...added.map(({ preview_url, ...p }: any) => p)] as any });
            return added;
          }}/>
      </CardSection>

      {/* ============ 2 · FIRST AID & EMERGENCY ============ */}
      <CardSection n="2" title="First aid & emergency response" done={s2Done}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <EmployeeSelect label="First aid officer"
            value={draft.first_aider_employee_id ?? ""}
            disabled={readOnly}
            onPick={(id, mobile, name) => patchDraft({
              first_aider_employee_id: id || null,
              first_aider_name: name ?? draft.first_aider_name,
              first_aider_contact: mobile ?? draft.first_aider_contact,
            })}/>
          <label className="block text-[12px] text-text-muted">Contact number
            <Input value={draft.first_aider_contact ?? ""} onChange={e => patchDraft({ first_aider_contact: e.target.value })} disabled={readOnly} placeholder="04xx xxx xxx"/>
          </label>
        </div>
        {!draft.first_aider_employee_id && (
          <label className="block text-[12px] text-text-muted">Or name (subbie / other)
            <Input value={draft.first_aider_name ?? ""} onChange={e => patchDraft({ first_aider_name: e.target.value })} disabled={readOnly} placeholder="First aider's name"/>
          </label>
        )}
        {draft.first_aider_employee_id && <FirstAidTicketCheck employeeId={draft.first_aider_employee_id}/>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TriState label="Suitable first aid kit on site?" value={draft.first_aid_kit_onsite ?? null} disabled={readOnly}
            onChange={v => patchDraft({ first_aid_kit_onsite: v })}
            noWarning="get one before work starts"/>
          <TriState label="Fire extinguisher on site + in date?" value={firstAid.extinguisher ?? null} disabled={readOnly}
            onChange={v => patchRisk({ first_aid: { ...firstAid, extinguisher: v } })}
            noWarning="required — especially for any hot work"/>
        </div>

        <PhotoStrip label="Photo of the first aid kit + extinguisher (proof they're on site + stocked)"
          photos={(firstAid.kit_photos as any[]) ?? []}
          disabled={readOnly}
          onAdd={async files => {
            const added = await uploadPhoto(files, `start-cards/${cardId}/first-aid`);
            patchRisk({ first_aid: { ...firstAid, kit_photos: [...((firstAid.kit_photos as any[]) ?? []), ...added.map(({ preview_url, ...p }: any) => p)] } });
            return added;
          }}/>

        <label className="block text-[12px] text-text-muted">Emergency evacuation / muster point
          <Input value={draft.evacuation_point ?? ""} onChange={e => patchDraft({ evacuation_point: e.target.value })} disabled={readOnly} placeholder="e.g. Outside compound gate, by the site sign"/>
        </label>

        <div className="bg-ink rounded-md border border-line/50 p-3 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-[10px] uppercase tracking-wider text-text-muted">Nearest medical facility — critical, every card</span>
            {gps.fix && (
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`hospital or medical centre near ${gps.fix.latitude},${gps.fix.longitude}`)}`}
                target="_blank" rel="noreferrer"
                className="text-[11px] text-accent hover:underline inline-flex items-center gap-1">
                <MapPin size={11}/> Find nearest on Maps <ExternalLink size={9}/>
              </a>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-[12px] text-text-muted">Hospital / medical centre + address
              <Input value={draft.nearest_hospital ?? ""} onChange={e => patchDraft({ nearest_hospital: e.target.value })} disabled={readOnly} placeholder="e.g. Mackay Base Hospital, 475 Bridge Rd, Mackay"/>
            </label>
            <label className="block text-[12px] text-text-muted">Phone
              <Input value={draft.nearest_hospital_phone ?? ""} onChange={e => patchDraft({ nearest_hospital_phone: e.target.value })} disabled={readOnly} placeholder="(07) …"/>
            </label>
          </div>
          {!readOnly && card.site_id && (draft.nearest_hospital || draft.evacuation_point) && (
            <button type="button"
              onClick={async () => {
                const { error } = await supabase.from("sites").update({
                  nearest_hospital: draft.nearest_hospital || null,
                  nearest_hospital_phone: draft.nearest_hospital_phone || null,
                  evacuation_point: draft.evacuation_point || null,
                }).eq("site_id", card.site_id!);
                window.alert(error ? `Couldn't save to the site: ${error.message}` : "Saved to the site profile — every future card here auto-fills.");
              }}
              className="text-[11px] text-accent hover:underline">
              Save hospital + evac point to this site's profile (fill once, never again)
            </button>
          )}
        </div>

        <div className="bg-ink rounded-md border border-line/50 p-3 text-[12px] space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Emergency numbers + response</div>
          <div className="flex justify-between"><span className="text-text-muted">Police · Fire · Ambulance</span><span className="font-mono">000</span></div>
          <div className="flex justify-between"><span className="text-text-muted">Before You Dig Australia</span><span className="font-mono">1100</span></div>
          <div className="flex justify-between"><span className="text-text-muted">Incident reporting</span><span>Supervisor → Coretel director (immediately)</span></div>
          <div className="text-[11px] text-text-muted pt-1.5 border-t border-line/40 mt-1.5">
            In an emergency: get everyone to the muster point → 000 if required → first aid → call the supervisor →
            report in the system. Preserve the scene beyond making it safe.
          </div>
        </div>
      </CardSection>

      {/* ============ 3 · RISK SCREENS ============ */}
      <CardSection n="3" title="Site risk screens" done={s3Done}
        badge={!s3Done ? `${RISK_SCREENS.filter(s => screens[s.key]?.answer == null).length} unanswered` : undefined}>
        <div className="text-[11px] text-text-muted -mt-1">Answer every one. A Yes expands what's needed and flags permits automatically.</div>
        {RISK_SCREENS.map(s => (
          <div key={s.key} className="border-b border-line/30 pb-3 last:border-0 last:pb-0">
            <YesNo label={s.q} value={screens[s.key]?.answer ?? null} disabled={readOnly}
              onChange={v => patchScreen(s.key, { answer: v })}/>
            {screens[s.key]?.answer === true && (
              <div className="mt-2 ml-1 pl-3 border-l-2 border-accent/40 space-y-2">
                {s.permit && (
                  <div className="text-[11px] text-warn inline-flex items-center gap-1.5">
                    <AlertTriangle size={11}/> Flags a {s.permit.replace(/_/g, " ")} permit — link it in section 4.
                  </div>
                )}
                {s.key === "plant_q" && <div className="text-[11px] text-text-muted">Detail the plant in section 6.</div>}
                {s.key === "public" && <div className="text-[11px] text-text-muted">Complete traffic management in section 7.</div>}
                {(s.detail ?? []).map(d => d.kind === "yesno" ? (
                  <YesNo key={d.key} small label={`${d.label}${d.required ? " *" : ""}`}
                    value={screens[s.key]?.[d.key] ?? null} disabled={readOnly}
                    onChange={v => patchScreen(s.key, { [d.key]: v })}/>
                ) : (
                  <label key={d.key} className="block text-[11px] text-text-muted">{d.label}{d.required ? " *" : ""}
                    <Input value={screens[s.key]?.[d.key] ?? ""} disabled={readOnly}
                      onChange={e => patchScreen(s.key, { [d.key]: e.target.value })}/>
                  </label>
                ))}
                {/* 8 Oct 2026 (Ops brief 6, gap 6) — what Ventia audits on the prestart
                    when anyone works under people at height (their SWMS 09 cl. 3-10, 20):
                    the comms method, the named ground observer, the zone sizes, and
                    everyone allowed in the inner zone with their position and exit route. */}
                {s.key === "exclusion" && (() => {
                  const ex = screens.exclusion ?? {};
                  const people: { name?: string; position?: string; exit_route?: string }[] = Array.isArray(ex.inner_people) ? ex.inner_people : [];
                  const setPeople = (next: typeof people) => patchScreen("exclusion", { inner_people: next });
                  return (
                    <div className="space-y-2 pt-1">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <label className="block text-[11px] text-text-muted">How the crew talks to each other *
                          <Select value={ex.comms_method ?? ""} disabled={readOnly} onChange={e => patchScreen("exclusion", { comms_method: e.target.value })}>
                            <option value="">Pick…</option>
                            <option value="radio">2-way radios</option>
                            <option value="phone">Phone</option>
                            <option value="voice">Voice / line of sight</option>
                          </Select>
                        </label>
                        <label className="block text-[11px] text-text-muted">Channel / detail
                          <Input value={ex.comms_detail ?? ""} disabled={readOnly} placeholder="e.g. channel 5, radio check done"
                            onChange={e => patchScreen("exclusion", { comms_detail: e.target.value })}/>
                        </label>
                      </div>
                      {ex.comms_method && ex.comms_method !== "radio" && (
                        <div className="text-[11px] text-warn">Coretel's rule is 2-way radios on every site — no shouted call-downs. Say why not in the detail.</div>
                      )}
                      <label className="block text-[11px] text-text-muted">Ground safety observer — named, and never also the haul hand *
                        <Input value={ex.observer ?? ""} disabled={readOnly} placeholder="Who is watching the zone today"
                          onChange={e => patchScreen("exclusion", { observer: e.target.value })}/>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="block text-[11px] text-text-muted">Inner zone (m) *
                          <Input inputMode="decimal" value={ex.inner_zone_m ?? ""} disabled={readOnly} placeholder="e.g. 15"
                            onChange={e => patchScreen("exclusion", { inner_zone_m: e.target.value })}/>
                        </label>
                        <label className="block text-[11px] text-text-muted">Wider zone
                          <Input value={ex.outer_zone ?? ""} disabled={readOnly} placeholder="e.g. 10 m from the fence"
                            onChange={e => patchScreen("exclusion", { outer_zone: e.target.value })}/>
                        </label>
                      </div>
                      <div className="text-[11px] text-text-muted">Inner zone: half the highest working point on a tower, one third on a pole or mast, never more than 20 m. Wider zone: 10 m from the compound fence or 15 m from the structure.</div>
                      <div>
                        <div className="text-[11px] text-text-muted mb-1">Anyone allowed in the inner zone while people are aloft — name, position and exit route</div>
                        {people.length === 0 && <div className="text-[11px] text-text-dim italic mb-1">Nobody — the inner zone stays empty while anyone is working above.</div>}
                        {people.map((pp, i) => (
                          <div key={i} className="flex items-start gap-1.5 mb-2"><div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 flex-1 min-w-0">
                            <Input value={pp.name ?? ""} disabled={readOnly} placeholder="Name" onChange={e => setPeople(people.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}/>
                            <Input value={pp.position ?? ""} disabled={readOnly} placeholder="Position (where they stand)" onChange={e => setPeople(people.map((x, j) => j === i ? { ...x, position: e.target.value } : x))}/>
                            <Input value={pp.exit_route ?? ""} disabled={readOnly} placeholder="Exit route" onChange={e => setPeople(people.map((x, j) => j === i ? { ...x, exit_route: e.target.value } : x))}/></div>
                            {!readOnly && <button type="button" onClick={() => setPeople(people.filter((_, j) => j !== i))} className="text-text-muted hover:text-danger p-1" aria-label="Remove"><X size={13}/></button>}
                          </div>
                        ))}
                        {!readOnly && <button type="button" onClick={() => setPeople([...people, { name: "", position: "", exit_route: "" }])} className="text-[11px] text-accent hover:underline inline-flex items-center gap-1"><Plus size={11}/> Add a person</button>}
                      </div>
                    </div>
                  );
                })()}
                {s.key === "heights" && screens.heights?.rescue_plan === false && (
                  <div className="text-[11px] text-danger font-medium">No rescue plan = no heights work. A suspended worker has minutes.</div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Environmental battery */}
        <div className="pt-1">
          <div className="text-[12px] text-text-muted mb-2">Environmental controls required today</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            {ENV_CONTROLS.map(c => (
              <YesNo key={c.key} small label={c.label} value={env[c.key] ?? null} disabled={readOnly}
                onChange={v => patchRisk({ environment: { ...env, [c.key]: v } })}/>
            ))}
          </div>
          {ENV_CONTROLS.some(c => env[c.key] === true) && (
            <label className="block text-[11px] text-text-muted mt-2">Controls in place for the Yes answers *
              <Textarea rows={2} value={env.controls_detail ?? ""} disabled={readOnly}
                onChange={e => patchRisk({ environment: { ...env, controls_detail: e.target.value } })}
                placeholder="e.g. Water cart for dust, bunded fuel storage, waste to skip for licensed disposal"/>
            </label>
          )}
          <div className="mt-2">
            <YesNo small label="Spill kit on site" value={env.spill_kit ?? null} disabled={readOnly}
              onChange={v => patchRisk({ environment: { ...env, spill_kit: v } })}/>
          </div>
        </div>
      </CardSection>

      {/* ============ 4 · PERMITS ============ */}
      <CardSection n="4" title="Permits" done={s4Done}
        badge={flaggedPermits.size > 0 && !s4Done ? `${flaggedPermits.size} required` : undefined}>
        {flaggedPermits.size === 0 && (
          <div className="text-[12px] text-text-muted italic">No permits flagged by the risk screens or tasks. If work changes today, come back — the screens re-flag automatically.</div>
        )}
        {[...flaggedPermits].map(pt => {
          const linked = permits.find(p => p.permit_type === pt);
          const candidates = projectPermits.filter((p: any) => p.permit_type === pt);
          return (
            <div key={pt} className="bg-ink rounded-md border border-line/50 p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-medium capitalize">{pt.replace(/_/g, " ")}</span>
                {linked?.permit_number
                  ? <Pill label={linked.permit_number} tone="ok" small/>
                  : <Pill label="not linked" tone="warning" small/>}
              </div>
              {!readOnly && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={linked?.permit_id ?? ""} className="flex-1 min-w-[220px]"
                    onChange={e => {
                      const p = projectPermits.find((x: any) => x.permit_id === e.target.value);
                      const next = permits.filter(x => x.permit_type !== pt);
                      if (p) next.push({ permit_type: pt, permit_id: p.permit_id, permit_number: p.permit_number });
                      patchRisk({ permits: next });
                    }}>
                    <option value="">{candidates.length ? "Link an existing permit…" : "No open permits of this type on the project"}</option>
                    {candidates.map((p: any) => (
                      <option key={p.permit_id} value={p.permit_id}>{p.permit_number} · {p.status} · {fmtDate(p.valid_from)} → {fmtDate(p.valid_until)}</option>
                    ))}
                  </Select>
                  <Link to={`/permits/issue/${pt}`} target="_blank"
                    className="text-[12px] text-accent hover:underline inline-flex items-center gap-1">
                    Issue now <ExternalLink size={10}/>
                  </Link>
                  <button type="button" onClick={() => refetchPermits()} className="text-[11px] text-text-muted hover:text-accent">refresh</button>
                </div>
              )}
            </div>
          );
        })}
      </CardSection>

      {/* ============ 5 · PPE ============ */}
      <CardSection n="5" title="PPE" done={s5Done}>
        <div className="bg-accent/8 border border-accent/25 rounded-md p-3">
          <div className="text-[10px] uppercase tracking-wider text-accent mb-1.5">Always mandatory on Coretel sites</div>
          <div className="text-[12px] text-text">{MANDATORY_PPE.join(" · ")}</div>
          <label className="flex items-center gap-2 text-[12px] mt-2.5 cursor-pointer">
            <input type="checkbox" checked={(draft.ppe as any)?.mandatory_confirmed ?? false} disabled={readOnly}
              onChange={e => patchDraft({ ppe: { ...(draft.ppe as any ?? {}), mandatory_confirmed: e.target.checked } })}/>
            All mandatory PPE is on site and being worn
          </label>
        </div>
        <div>
          <div className="text-[12px] text-text-muted mb-1.5">Additional PPE required by today's SWMS / tasks</div>
          <div className="flex flex-wrap gap-1.5">
            {EXTRA_PPE.map(p => {
              const on = ((draft.ppe as any)?.extras ?? []).includes(p);
              return (
                <button key={p} type="button" disabled={readOnly}
                  onClick={() => {
                    const extras = ((draft.ppe as any)?.extras ?? []) as string[];
                    patchDraft({ ppe: { ...(draft.ppe as any ?? {}), extras: on ? extras.filter(x => x !== p) : [...extras, p] } });
                  }}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition ${on ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
                  {p}
                </button>
              );
            })}
          </div>
        </div>
        <label className="block text-[12px] text-text-muted">Other safety equipment
          <Input value={(draft.ppe as any)?.other ?? ""} disabled={readOnly}
            onChange={e => patchDraft({ ppe: { ...(draft.ppe as any ?? {}), other: e.target.value } })}
            placeholder="e.g. Gas monitor, rescue kit, insulated gloves"/>
        </label>
      </CardSection>

      {/* ============ 6 · PLANT & EQUIPMENT ============ */}
      <CardSection n="6" title="Plant & equipment" done={s6Done}
        badge={screens.plant_q?.answer === true && ((draft.plant as any)?.items ?? []).length === 0 ? "detail needed" : undefined}>
        {screens.plant_q?.answer !== true ? (
          <div className="text-[12px] text-text-muted italic">Answer "Plant on site today?" in the risk screens — a Yes opens this section.</div>
        ) : (
          <>
            <div>
              <div className="text-[12px] text-text-muted mb-1.5">Plant on site — each item needs its pre-start done before use</div>
              {(((draft.plant as any)?.items ?? []) as any[]).map((it, i) => (
                <div key={i} className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <Input className="flex-1 min-w-[160px]" value={it.name} disabled={readOnly} placeholder="e.g. 5t excavator"
                    onChange={e => {
                      const items = [...(((draft.plant as any)?.items ?? []) as any[])];
                      items[i] = { ...items[i], name: e.target.value };
                      patchDraft({ plant: { ...(draft.plant as any ?? {}), used: true, items } });
                    }}/>
                  <button type="button" disabled={readOnly}
                    onClick={() => {
                      const items = [...(((draft.plant as any)?.items ?? []) as any[])];
                      items[i] = { ...items[i], prestart_done: !items[i].prestart_done };
                      patchDraft({ plant: { ...(draft.plant as any ?? {}), items } });
                    }}
                    className={`text-[11px] px-2.5 py-1.5 rounded border transition ${it.prestart_done ? "bg-ok/15 border-ok text-ok" : "border-warn/60 text-warn"}`}>
                    {it.prestart_done ? "Pre-start done" : "Pre-start NOT done"}
                  </button>
                  {!readOnly && (
                    <button type="button" onClick={() => {
                      const items = (((draft.plant as any)?.items ?? []) as any[]).filter((_, ii) => ii !== i);
                      patchDraft({ plant: { ...(draft.plant as any ?? {}), items } });
                    }} className="text-text-muted hover:text-danger p-1"><Trash2 size={13}/></button>
                  )}
                </div>
              ))}
              {!readOnly && (
                <Button size="sm" variant="secondary" icon={Plus}
                  onClick={() => patchDraft({ plant: { ...(draft.plant as any ?? {}), used: true, items: [...(((draft.plant as any)?.items ?? []) as any[]), { name: "", prestart_done: false }] } })}>
                  Add plant
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <YesNo small label="Safety devices fitted + working on all plant" value={(draft.plant as any)?.safety_devices ?? null} disabled={readOnly}
                onChange={v => patchDraft({ plant: { ...(draft.plant as any ?? {}), safety_devices: v } })}/>
              <YesNo small label="Operator competencies / VOCs on record" value={(draft.plant as any)?.competency_records ?? null} disabled={readOnly}
                onChange={v => patchDraft({ plant: { ...(draft.plant as any ?? {}), competency_records: v } })}/>
            </div>
            <HazardRows label="Plant hazards + controls (undergrounds → BYDA + locator, overheads → spotter + exclusion…)"
              rows={((draft.plant as any)?.hazards ?? []) as any[]} disabled={readOnly}
              onChange={rows => patchDraft({ plant: { ...(draft.plant as any ?? {}), hazards: rows } })}/>
          </>
        )}
      </CardSection>

      {/* ============ 7 · TRAFFIC MANAGEMENT ============ */}
      <CardSection n="7" title="Traffic management" done={s7Done}
        badge={screens.public?.answer === true && !(draft.traffic as any)?.tgs_ref ? "TGS needed" : undefined}>
        {screens.public?.answer !== true ? (
          <div className="text-[12px] text-text-muted italic">Answer "Public interface or traffic today?" in the risk screens — a Yes opens this section.</div>
        ) : (
          <>
            <label className="block text-[12px] text-text-muted">TGS / TMP reference in use *
              <Input value={(draft.traffic as any)?.tgs_ref ?? ""} disabled={readOnly}
                onChange={e => patchDraft({ traffic: { ...(draft.traffic as any ?? {}), tgs_ref: e.target.value } })}
                placeholder="e.g. TGS 14 — works outside traffic lane, or external TMP ref"/>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block text-[12px] text-text-muted">Implemented by (+ ticket no. if TC)
                <Input value={(draft.traffic as any)?.implemented_by ?? ""} disabled={readOnly}
                  onChange={e => patchDraft({ traffic: { ...(draft.traffic as any ?? {}), implemented_by: e.target.value } })}/>
              </label>
              <label className="block text-[12px] text-text-muted">Verified / monitored by
                <Input value={(draft.traffic as any)?.verified_by ?? ""} disabled={readOnly}
                  onChange={e => patchDraft({ traffic: { ...(draft.traffic as any ?? {}), verified_by: e.target.value } })}/>
              </label>
            </div>
            <PhotoStrip label="Photos of traffic controls in place"
              photos={((draft.traffic as any)?.photos ?? []) as any[]}
              disabled={readOnly}
              onAdd={async files => {
                const added = await uploadPhoto(files, `start-cards/${cardId}/traffic`);
                patchDraft({ traffic: { ...(draft.traffic as any ?? {}), photos: [...(((draft.traffic as any)?.photos ?? []) as any[]), ...added.map(({ preview_url, ...p }: any) => p)] } });
                return added;
              }}/>
            <div className="text-[11px] text-text-muted">Inspect signs + devices at least every 4 hours during works, and after weather or any change.</div>
          </>
        )}
      </CardSection>

      {/* ============ 8 · SITE-SPECIFIC ============ */}
      <CardSection n="8" title="Site-specific checks" done={s8Done}>
        <YesNo label="Start-of-shift visual inspection done — all controls effective, no new hazards"
          value={screens.visual?.answer ?? null} disabled={readOnly}
          onChange={v => patchScreen("visual", { answer: v })}/>
        {screens.visual?.answer === false && (
          <div className="text-[11px] text-danger">Fix what the walk found before work starts — note it below.</div>
        )}
        <HazardRows label="Anything today not covered by the SWMS — hazard + control"
          rows={(draft.extra_hazards as any[]) ?? []} disabled={readOnly}
          onChange={rows => patchDraft({ extra_hazards: rows })}/>
      </CardSection>

      {/* ============ 9 · SIGN ON ============ */}
      <CardSection n="9" title={`Sign on${signons.length ? ` · ${signons.length}` : ""}`} done={s9Done} defaultOpen={!readOnly}>
        <div className="bg-ink rounded-md border border-line/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">By signing on, each person certifies:</div>
          <ul className="text-[12px] text-text-muted space-y-1 leading-relaxed">
            {DECLARATION.map((d, i) => <li key={i} className="flex gap-2"><span className="text-accent">·</span>{d}</li>)}
          </ul>
        </div>
        {signons.map(s => (
          <div key={s.signon_id} className="bg-ink rounded-md border border-line/50 px-3 py-2 flex items-center gap-3">
            <CheckCircle2 size={14} className="text-ok flex-shrink-0"/>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-text">{s.name}{s.company ? <span className="text-text-muted"> · {s.company}</span> : ""}</div>
              <div className="text-[11px] text-text-muted">{s.role_title ?? "—"} · signed {fmtTime(s.signed_at)}</div>
            </div>
            {s.signature_svg && (
              <span className="w-[90px] h-[30px] bg-white rounded border border-line overflow-hidden [&>svg]:w-full [&>svg]:h-full"
                dangerouslySetInnerHTML={{ __html: s.signature_svg }}/>
            )}
          </div>
        ))}
        {!readOnly && <SignonAdder cardId={cardId} gps={gps.fix} onAdded={() => { refetchSignons(); }}/>}
      </CardSection>

      {/* Sticky actions */}
      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-[880px] w-full">
          {!readOnly ? (
            <>
              <Button variant="secondary" size="lg" onClick={() => void saveDraft()}>
                {saveState === "saving" ? <Loader2 size={14} className="animate-spin"/> : "Save draft"}
              </Button>
              <Button size="lg" icon={ShieldCheck} onClick={submitCard} className="flex-1 justify-center">
                Submit start card
              </Button>
            </>
          ) : (
            <Button variant="secondary" size="lg" onClick={() => navigate(returnTo ?? "/hse/start-card")} className="flex-1 justify-center">
              Back to start cards
            </Button>
          )}
        </div>
      </StickyBottomBar>
    </div>
  );
}

// =============================================================================
// Building blocks
// =============================================================================
function CardSection({ n, title, done, badge, defaultOpen = false, children }: {
  n: string; title: string; done: boolean; badge?: string; defaultOpen?: boolean; children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-ink-raised/40 transition">
        <span className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 text-[11px] font-mono ${done ? "bg-ok/15 border-ok text-ok" : "border-line text-text-muted"}`}>
          {done ? <CheckCircle2 size={13}/> : n}
        </span>
        <span className="text-[14px] font-medium flex-1">{title}</span>
        {badge && <Pill label={badge} tone="warning" small/>}
        <ChevronDown size={15} className={`text-text-muted transition-transform ${open ? "" : "-rotate-90"}`}/>
      </button>
      {open && <div className="px-4 pb-4 space-y-4 border-t border-line/40 pt-4">{children}</div>}
    </Card>
  );
}

function YesNo({ label, value, onChange, disabled, small = false }: {
  label: string; value: boolean | null; onChange: (v: boolean) => void; disabled?: boolean; small?: boolean;
}) {
  return (
    <div className={`flex items-center justify-between gap-3 ${small ? "" : "py-0.5"}`}>
      <span className={`${small ? "text-[12px]" : "text-[13px]"} text-text flex-1`}>{label}</span>
      <div className="flex gap-1.5 flex-shrink-0">
        {[true, false].map(v => (
          <button key={String(v)} type="button" disabled={disabled} onClick={() => onChange(v)}
            className={`${small ? "text-[11px] px-2.5 py-1" : "text-[12px] px-3.5 py-1.5"} rounded-full border transition ${
              value === v
                ? v ? "bg-ok/15 border-ok text-ok" : "bg-warn/15 border-warn text-warn"
                : "border-line text-text-muted hover:text-text"}`}>
            {v ? "Yes" : "No"}
          </button>
        ))}
      </div>
    </div>
  );
}

function TriState({ label, value, onChange, disabled, noWarning }: {
  label: string; value: boolean | null; onChange: (v: boolean) => void; disabled?: boolean; noWarning?: string;
}) {
  return (
    <div>
      <YesNo label={label} value={value} onChange={onChange} disabled={disabled}/>
      {value === false && noWarning && <div className="text-[11px] text-warn mt-1">{noWarning}</div>}
    </div>
  );
}

function HazardRows({ label, rows, onChange, disabled }: {
  label: string; rows: { hazard: string; control: string }[]; onChange: (r: any[]) => void; disabled?: boolean;
}) {
  return (
    <div>
      <div className="text-[12px] text-text-muted mb-1.5">{label}</div>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 mb-1.5 flex-wrap">
          <Input className="flex-1 min-w-[140px]" placeholder="Hazard / risk" value={r.hazard} disabled={disabled}
            onChange={e => { const next = [...rows]; next[i] = { ...next[i], hazard: e.target.value }; onChange(next); }}/>
          <Input className="flex-1 min-w-[140px]" placeholder="Control / measure" value={r.control} disabled={disabled}
            onChange={e => { const next = [...rows]; next[i] = { ...next[i], control: e.target.value }; onChange(next); }}/>
          {!disabled && (
            <button type="button" onClick={() => onChange(rows.filter((_, ri) => ri !== i))}
              className="text-text-muted hover:text-danger p-1"><Trash2 size={13}/></button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button size="sm" variant="secondary" icon={Plus} onClick={() => onChange([...rows, { hazard: "", control: "" }])}>Add row</Button>
      )}
    </div>
  );
}

function EmployeeSelect({ label, value, onPick, disabled }: {
  label: string; value: string; disabled?: boolean;
  onPick: (id: string, mobile: string | null, name?: string | null) => void;
}) {
  const { data: employees = [] } = useEmployees();
  return (
    <label className="block text-[12px] text-text-muted">{label}
      <Select value={value} disabled={disabled}
        onChange={e => {
          const emp = employees.find(x => x.employee_id === e.target.value);
          onPick(e.target.value, emp?.mobile ?? null, emp?.full_name ?? null);
        }}>
        <option value="">Pick…</option>
        {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.full_name}</option>)}
      </Select>
    </label>
  );
}

// Broad first-aid match: coded types (FIRST-AID / CPR) OR anything whose
// display name reads first-aid-ish (types drift; the register is the truth).
function FirstAidTicketCheck({ employeeId }: { employeeId: string }) {
  const { data } = useQuery({
    queryKey: ["first_aid_check", employeeId],
    queryFn: async () => {
      const { data } = await supabase.from("employee_licences")
        .select("expiry_date, is_active, licence_types:licence_type_id(type_code, display_name)")
        .eq("employee_id", employeeId).eq("is_active", true);
      const rows = (data ?? []) as any[];
      const firstAidRows = rows.filter(l => {
        const code = l.licence_types?.type_code ?? "";
        const name = (l.licence_types?.display_name ?? "").toLowerCase();
        return ["FIRST-AID", "CPR"].includes(code) || name.includes("first aid") || name.includes("hltaid") || name.includes("cpr");
      });
      const today = new Date().toISOString().slice(0, 10);
      const current = firstAidRows.find(l => !l.expiry_date || l.expiry_date >= today);
      if (current) return { state: "current" as const, expiry: current.expiry_date };
      const expired = firstAidRows.sort((a, b) => (b.expiry_date ?? "").localeCompare(a.expiry_date ?? ""))[0];
      if (expired) return { state: "expired" as const, expiry: expired.expiry_date };
      return { state: "none" as const };
    },
  });
  if (!data) return null;
  if (data.state === "current") {
    return <div className="text-[12px] text-ok inline-flex items-center gap-1.5"><CheckCircle2 size={13}/> Current first-aid ticket on file{data.expiry ? ` (expires ${fmtDate(data.expiry)})` : ""}.</div>;
  }
  if (data.state === "expired") {
    return (
      <div className="text-[12px] text-warn inline-flex items-center gap-1.5 flex-wrap">
        <AlertTriangle size={13}/> First-aid ticket EXPIRED {fmtDate(data.expiry)} —
        <Link to={`/hr/licences/${employeeId}`} className="text-accent hover:underline">renew it in the register</Link>.
      </div>
    );
  }
  return (
    <div className="text-[12px] text-warn inline-flex items-center gap-1.5 flex-wrap">
      <AlertTriangle size={13}/> No first-aid ticket on file —
      <Link to={`/hr/licences/${employeeId}`} className="text-accent hover:underline">check their licences</Link> or pick someone else.
    </div>
  );
}

function SwmsPicker({ value, onChange, disabled, projectId }: {
  value: SwmsPick[]; onChange: (v: SwmsPick[]) => void; disabled?: boolean; projectId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data: library = [] } = useQuery({
    queryKey: ["swms_library_active"],
    queryFn: async () => {
      const { data } = await supabase.from("swms_library")
        .select("swms_id, swms_code, display_name, is_active").eq("is_active", true).order("swms_code");
      return (data ?? []) as any[];
    },
  });

  // T-427 — this project's APPROVED site SWMS. These are what the crew should
  // actually be signing onto: written for this structure, this access, these
  // conditions. Drafts are excluded on purpose — an unapproved SWMS must
  // never reach a crew.
  const { data: siteSwms = [] } = useQuery({
    queryKey: ["start_card_site_swms", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await supabase.from("project_swms")
        .select("project_swms_id, swms_number, title, version, base_swms_id, base:base_swms_id(swms_code)")
        .eq("project_id", projectId!)
        .eq("status", "approved")
        .order("approved_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const chosen = new Set(value.map(v => v.swms_id));
  const chosenSite = new Set(value.map(v => v.project_swms_id).filter(Boolean) as string[]);
  const ql = q.trim().toLowerCase();
  const siteHits = siteSwms.filter(s => !chosenSite.has(s.project_swms_id)
    && (!ql || `${s.swms_number} ${s.title}`.toLowerCase().includes(ql)));
  const hits = library.filter(s => !chosen.has(s.swms_id) && (!ql || `${s.swms_code} ${s.display_name}`.toLowerCase().includes(ql)));

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12px] text-text-muted">SWMS for today's work</span>
        {!disabled && !open && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setOpen(true)}>Add SWMS</Button>}
      </div>
      {value.length === 0 && <div className="text-[12px] text-text-dim italic">None selected — high-risk work needs its SWMS on the card.</div>}
      <div className="flex flex-wrap gap-1.5">
        {value.map((s, i) => (
          <span key={s.project_swms_id ?? s.swms_id ?? i}
            className={`inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1.5 py-1 text-[12px] border ${
              s.site_specific ? "bg-ok/10 border-ok/40" : "bg-ink border-line/60"}`}>
            <FileText size={11} className={s.site_specific ? "text-ok" : "text-accent"}/>
            <span className={`font-mono text-[11px] ${s.site_specific ? "text-ok" : "text-accent"}`}>{s.swms_code}</span>
            <span className="max-w-[220px] truncate">{s.display_name}</span>
            {s.site_specific && <span className="text-[10px] uppercase tracking-wide text-ok">site</span>}
            {!disabled && (
              <button type="button"
                onClick={() => onChange(value.filter(v => (s.project_swms_id
                  ? v.project_swms_id !== s.project_swms_id
                  : v.swms_id !== s.swms_id || v.project_swms_id)))}
                className="text-text-muted hover:text-danger p-0.5"><X size={11}/></button>
            )}
          </span>
        ))}
      </div>
      {/* Nudge: an approved site SWMS exists but the card is only carrying
          generic templates. */}
      {!disabled && siteSwms.length > 0 && value.length > 0 && !value.some(v => v.site_specific) && (
        <div className="text-[11px] text-warn mt-1.5">
          <AlertTriangle size={11} className="inline mr-1 -mt-0.5"/>
          This project has an approved site-specific SWMS — use that on the card instead of the generic template.
        </div>
      )}
      {open && (
        <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3 mt-2 space-y-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"/>
            <Input className="pl-8" placeholder="Search the SWMS library…" value={q} onChange={e => setQ(e.target.value)} autoFocus/>
          </div>
          <div className="space-y-1 max-h-[220px] overflow-y-auto">
            {/* Site-specific first — written for THIS job. */}
            {siteHits.length > 0 && (
              <div className="text-[10px] uppercase tracking-wider text-ok px-2 pt-1 pb-0.5">
                Approved for this project — use these
              </div>
            )}
            {siteHits.map(s => (
              <button key={s.project_swms_id} type="button"
                onClick={() => {
                  onChange([...value, {
                    swms_id: s.base_swms_id ?? s.project_swms_id,
                    swms_code: s.swms_number,
                    display_name: s.title,
                    version: s.version != null ? `v${s.version}` : null,
                    project_swms_id: s.project_swms_id,
                    site_specific: true,
                  }]);
                  setQ("");
                }}
                className="w-full text-left px-2.5 py-1.5 rounded hover:bg-ok/10 text-[12px] flex items-center gap-2 border border-ok/30 bg-ok/5">
                <span className="font-mono text-[11px] text-ok w-[110px] flex-shrink-0">{s.swms_number}</span>
                <span className="truncate flex-1">{s.title}</span>
                <span className="text-[10px] text-ok flex-shrink-0">v{s.version}</span>
              </button>
            ))}
            {siteHits.length > 0 && hits.length > 0 && (
              <div className="text-[10px] uppercase tracking-wider text-text-muted px-2 pt-2 pb-0.5">Generic templates</div>
            )}
            {hits.map(s => (
              <button key={s.swms_id} type="button"
                onClick={() => { onChange([...value, { swms_id: s.swms_id, swms_code: s.swms_code, display_name: s.display_name }]); setQ(""); }}
                className="w-full text-left px-2.5 py-1.5 rounded hover:bg-ink text-[12px] flex items-center gap-2">
                <span className="font-mono text-[11px] text-accent w-[100px] flex-shrink-0">{s.swms_code}</span>
                <span className="truncate">{s.display_name}</span>
              </button>
            ))}
            {hits.length === 0 && siteHits.length === 0 && <div className="text-[12px] text-text-dim italic px-2 py-1">No matches.</div>}
          </div>
          <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-text-muted hover:text-accent">Done</button>
        </div>
      )}
    </div>
  );
}

function PhotoStrip({ label, photos, onAdd, disabled }: {
  label: string; photos: any[]; disabled?: boolean;
  onAdd: (files: File[]) => Promise<any[]>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [previews, setPreviews] = useState<Record<string, string>>({});

  async function handle(files: FileList | null) {
    if (!files || files.length === 0) return;
    setBusy(true);
    try {
      const added = await onAdd(Array.from(files));
      const next: Record<string, string> = {};
      for (const a of added) if (a.preview_url) next[a.path] = a.preview_url;
      setPreviews(prev => ({ ...prev, ...next }));
    } catch (e: any) {
      window.alert(`Photo upload failed: ${e?.message ?? e}`);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div>
      <div className="text-[12px] text-text-muted mb-1.5">{label}</div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={e => handle(e.target.files)} disabled={busy}/>
      <div className="flex items-center gap-2 flex-wrap">
        {!disabled && (
          <Button size="sm" variant="secondary" icon={busy ? Loader2 : Camera} onClick={() => inputRef.current?.click()} disabled={busy}>
            {busy ? "Uploading…" : "Add photos"}
          </Button>
        )}
        <span className="text-[11px] text-text-muted">{photos.length} added{photos.some((p: any) => p.queued_offline) ? " (some queued)" : ""}</span>
      </div>
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {photos.map((p: any) => previews[p.path]
            ? <img key={p.path} src={previews[p.path]} alt="" className="w-16 h-16 object-cover rounded border border-line"/>
            : <div key={p.path} className="w-16 h-16 rounded border border-line bg-ink flex items-center justify-center text-[9px] text-text-muted text-center px-1">photo</div>)}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sign-on adder — person picker + signature, saved immediately
// =============================================================================
function SignonAdder({ cardId, gps, onAdded }: { cardId: string; gps: any; onAdded: () => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState<{ name: string; company: string; source: string; employee_id?: string; worker_id?: string } | null>(null);
  const [role, setRole] = useState("");
  const [otherName, setOtherName] = useState("");
  const [otherCompany, setOtherCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sigRef = useRef<SignaturePadHandle | null>(null);

  const { data: employees = [] } = useEmployees();
  const { data: subbies = [] } = useQuery({
    queryKey: ["picker_subbie_workers"],
    queryFn: async () => {
      const { data } = await supabase.from("subbie_workers")
        .select("worker_id, first_name, last_name, company:subcontractor_company_id(trading_name, legal_name)")
        .eq("is_active", true).order("last_name");
      return (data ?? []) as any[];
    },
  });

  const ql = q.trim().toLowerCase();
  const empHits = employees.filter(e => !ql || e.full_name.toLowerCase().includes(ql)).slice(0, 5);
  const subHits = subbies.filter(w => !ql || `${w.first_name} ${w.last_name}`.toLowerCase().includes(ql)).slice(0, 5);

  async function save() {
    if (!pick) return;
    if (!sigRef.current || sigRef.current.isEmpty()) { setErr("Signature required — that's the whole point."); return; }
    setBusy(true); setErr(null);
    try {
      const { error } = await supabase.from("hse_start_card_signons").insert({
        card_id: cardId,
        name: pick.name,
        company: pick.company || null,
        source: pick.source,
        employee_id: pick.employee_id ?? null,
        worker_id: pick.worker_id ?? null,
        role_title: role.trim() || null,
        signature_svg: sigRef.current.getSvg(),
        gps_lat: gps?.latitude,
        gps_lng: gps?.longitude,
        device_info: { ua: navigator.userAgent },
      });
      if (error) throw error;
      setPick(null); setRole(""); setQ(""); setOtherName(""); setOtherCompany("");
      onAdded();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return <Button size="sm" variant="secondary" icon={Plus} onClick={() => setOpen(true)}>Sign someone on</Button>;
  }

  return (
    <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3 space-y-3">
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      {!pick ? (
        <>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"/>
            <Input className="pl-8" placeholder="Search crew + subbie workers…" value={q} onChange={e => setQ(e.target.value)} autoFocus/>
          </div>
          <div className="space-y-1 max-h-[170px] overflow-y-auto">
            {empHits.map(e => (
              <button key={e.employee_id} type="button"
                onClick={() => setPick({ name: e.full_name, company: "Coretel", source: "employee", employee_id: e.employee_id })}
                className="w-full text-left px-2.5 py-1.5 rounded hover:bg-ink text-[12px] flex items-center justify-between">
                <span>{e.full_name}</span><span className="text-[10px] text-accent font-mono">CORETEL</span>
              </button>
            ))}
            {subHits.map(w => {
              const company = w.company?.trading_name ?? w.company?.legal_name ?? "Subbie";
              return (
                <button key={w.worker_id} type="button"
                  onClick={() => setPick({ name: `${w.first_name} ${w.last_name}`, company, source: "subbie", worker_id: w.worker_id })}
                  className="w-full text-left px-2.5 py-1.5 rounded hover:bg-ink text-[12px] flex items-center justify-between">
                  <span>{w.first_name} {w.last_name}</span><span className="text-[10px] text-text-muted font-mono truncate max-w-[120px]">{company.toUpperCase()}</span>
                </button>
              );
            })}
          </div>
          <div className="border-t border-line/40 pt-2.5 flex gap-2 flex-wrap items-end">
            <Input className="flex-1 min-w-[130px]" placeholder="Visitor name" value={otherName} onChange={e => setOtherName(e.target.value)}/>
            <Input className="flex-1 min-w-[110px]" placeholder="Company" value={otherCompany} onChange={e => setOtherCompany(e.target.value)}/>
            <Button size="sm" disabled={!otherName.trim()}
              onClick={() => setPick({ name: otherName.trim(), company: otherCompany.trim(), source: "other" })}>
              Next
            </Button>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-text-muted hover:text-accent">Cancel</button>
        </>
      ) : (
        <>
          <div className="text-[13px] font-medium">{pick.name} {pick.company && <span className="text-text-muted font-normal">· {pick.company}</span>}</div>
          <label className="block text-[12px] text-text-muted">Role today
            <Input value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Rigger, Excavator operator, Visitor"/>
          </label>
          <div>
            <div className="text-[12px] text-text-muted mb-1.5">Signature — read the declaration above first</div>
            <SignaturePad ref={sigRef} width={420} height={130}/>
            <button type="button" onClick={() => sigRef.current?.clear()} className="text-[11px] text-text-muted hover:text-accent mt-1">Clear</button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={busy} icon={busy ? Loader2 : CheckCircle2}>{busy ? "Saving…" : "Sign on"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setPick(null)}>Back</Button>
          </div>
        </>
      )}
    </div>
  );
}
